import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { App, Project } from '@hangar/shared';
import { useBuilds, useProfiles, useProviderAccounts } from '@/api/hooks';
import { formatDate } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { useRunBuildGate } from '@/lib/permissions';
import { useSeesProviders } from '@/lib/useAudience';
import { useRunBuild } from '@/shell/RunBuildContext';
import { CopyButton } from '@/components/CopyButton';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { FRAMEWORK_LABEL, Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BuildsList } from '@/features/builds/BuildsList';
import { ProfilesTable } from '@/features/profiles/ProfilesTable';
import { useProfileActions } from '@/features/profiles/useProfileActions';
import { BuildSetupCard } from './BuildSetupCard';
import { buildSetupText } from '@/features/apps/appsTableModel';
import { RepositoryCard } from './RepositoryCard';
import { BuildTriggersCard } from './BuildTriggersCard';
import { RepoFilesCard } from '@/features/repo-files/RepoFilesCard';

interface Props {
  app: App;
  project: Project;
  onEdit: () => void;
  onTab: (tab: string) => void;
}

/**
 * Design 29 · Overview: the app's settings, its build profiles and its latest builds. The hangar.yml card returns with
 * the hangar.yml sync of docs/mvp-plan.md P9 (deferred).
 */
export function AppOverview({ app, project, onEdit, onTab }: Props) {
  return (
    <div className="flex flex-col gap-5">
      <SettingsCard app={app} onEdit={onEdit} />
      <BuildSetupCard app={app} />
      <RepositoryCard app={app} />
      <RepoFilesCard app={app} />
      <ProfilesCard app={app} project={project} onTab={onTab} />
      <BuildTriggersCard app={app} project={project} />
      <RecentBuilds app={app} project={project} />
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 max-sm:flex-row max-sm:items-center max-sm:justify-between max-sm:gap-3 max-sm:border-b max-sm:pb-3 max-sm:last:border-b-0 max-sm:last:pb-0">
      <dt className="shrink-0 text-[13px] text-muted-foreground">{label}</dt>
      <dd className="min-w-0 max-sm:text-right">{children}</dd>
    </div>
  );
}

const Mono = ({ children, title }: { children: ReactNode; title?: string }) => (
  <span className="block truncate font-mono text-[13px] text-foreground" title={title}>
    {children}
  </span>
);

/** The EAS project id / Bitrise app slug with its copy button, or "not linked" (operators); clients read "Build setup: Ready". */
function ProviderIdRow({ app }: { app: App }) {
  const operator = useSeesProviders();
  if (!operator) {
    return (
      <Row label="Build setup">
        <span className="text-[14px] text-foreground" data-testid="app-build-setup-state">{buildSetupText(app)}</span>
      </Row>
    );
  }
  const providerId = app.easProjectId ?? app.bitriseAppSlug;
  return (
    <Row label={app.bitriseAppSlug && !app.easProjectId ? 'Bitrise app' : 'EAS project id'}>
      {app.easProjectId || app.bitriseAppSlug ? (
        <span className="flex min-w-0 items-center gap-1.5 max-sm:justify-end">
          <Mono title={providerId ?? undefined}>{providerId}</Mono>
          <CopyButton text={providerId!} size="icon-sm" label={app.easProjectId ? 'Copy EAS project id' : 'Copy Bitrise app slug'} testId="app-copy-provider-id" />
        </span>
      ) : (
        <span className="text-[13px] text-subtle">not linked</span>
      )}
    </Row>
  );
}

function SettingsFacts({ app }: { app: App }) {
  return (
    <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
      <Row label="App name">
        <span className="block truncate text-[14px] font-medium text-foreground">{app.name}</span>
      </Row>
      <Row label="Framework">
        <span className="text-[14px] text-foreground">{FRAMEWORK_LABEL[app.framework]}</span>
      </Row>
      <Row label="Working directory">
        <Tag className="max-w-full text-[13px] text-foreground">
          <span className="truncate">{app.workingDirectory}</span>
        </Tag>
      </Row>
      {/* "Config file" (hangar.yml) returns with docs/mvp-plan.md P9. */}
      <ProviderIdRow app={app} />
      <Row label="Slug">
        <Mono>{app.slug}</Mono>
      </Row>
    </dl>
  );
}

/** "Application configuration": what `PATCH /apps/{id}` edits, plus the facts that never change. */
function SettingsCard({ app, onEdit }: { app: App; onEdit: () => void }) {
  return (
    <Card className="enter" style={stagger(1)} data-testid="app-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="settings" size={18} className="text-subtle" />
          Application configuration
        </CardTitle>
        <CardDescription className="font-mono text-xs text-subtle">created {formatDate(app.createdAt)}{app.lastBuildNumber ? ` · last build #${app.lastBuildNumber}` : ''}</CardDescription>
        <CardAction>
          <Button variant="ghost" size="sm" data-testid="app-settings-edit" onClick={onEdit}>
            <Icon name="edit" size={16} />
            Edit
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <SettingsFacts app={app} />
      </CardContent>
    </Card>
  );
}

type ProfilesQuery = ReturnType<typeof useProfiles>;
type ProfileActions = ReturnType<typeof useProfileActions>;

function ProfilesLoading() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      {[0, 1].map((i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function ProfilesBody({ project, profiles, actions, onTab }: { project: Project; profiles: ProfilesQuery; actions: ProfileActions; onTab: (tab: string) => void }) {
  if (profiles.isLoading && !profiles.data) return <ProfilesLoading />;
  if (profiles.isError && !profiles.data) return <ErrorState compact error={profiles.error} onRetry={() => void profiles.refetch()} />;
  if (!profiles.data || profiles.data.length === 0) {
    return <EmptyState compact testId="app-profiles-empty" icon="tune" title="No build profiles yet" description="A profile says how the app is built: Android or iOS, the environments it may build for, and what each build produces." />;
  }
  return (
    <div className="flex flex-col gap-3">
      <ProfilesTable profiles={profiles.data} project={project} actionsFor={actions.actionsFor} />
      <button type="button" onClick={() => onTab('profiles')} className="inline-flex w-fit items-center gap-1 rounded-sm text-[13px] text-muted-foreground hover:text-foreground">
        Run a profile on the Profiles tab
        <Icon name="arrow_forward" size={16} />
      </button>
    </div>
  );
}

/** Build profiles (design 29): the compact table with Edit · Duplicate · Archive and New profile; Run lives on the Profiles tab. */
function ProfilesCard({ app, project, onTab }: { app: App; project: Project; onTab: (tab: string) => void }) {
  const profiles = useProfiles(app.id);
  const accounts = useProviderAccounts();
  const actions = useProfileActions({ app, project, accounts: accounts.data });
  return (
    <Card className="enter" style={stagger(2)} data-testid="app-profiles-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Build profiles {profiles.data ? <Tag className="tabular">{profiles.data.length}</Tag> : null}
        </CardTitle>
        <CardDescription>How each build is made: Android or iOS, its environments and what it produces</CardDescription>
        {actions.can.hidden ? null : (
          <CardAction>
            <GatedButton variant="outline" data-testid="app-profiles-new" can={actions.can} onClick={actions.create}>
              <Icon name="add" size={18} />
              New profile
            </GatedButton>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <ProfilesBody project={project} profiles={profiles} actions={actions} onTab={onTab} />
      </CardContent>
      {actions.overlays}
    </Card>
  );
}

function RecentBuildsHeader({ app, project }: { app: App; project: Project }) {
  return (
    <CardHeader>
      <CardTitle>Recent builds</CardTitle>
      <CardDescription>The latest builds of {app.slug}, every profile and environment</CardDescription>
      <CardAction>
        <Button asChild variant="ghost" size="sm">
          <Link to="/projects/$slug/builds" params={{ slug: project.slug }} search={{ app: app.slug }} data-testid="app-builds-all">
            All builds
            <Icon name="arrow_forward" size={16} />
          </Link>
        </Button>
      </CardAction>
    </CardHeader>
  );
}

/** The app's five latest builds (the Builds list, filtered); the whole history is one link away. */
function RecentBuilds({ app, project }: { app: App; project: Project }) {
  const builds = useBuilds({ projectId: project.id, app: app.slug, limit: 5 });
  const rows = builds.data?.pages[0]?.data;
  const gate = useRunBuildGate(project.id);
  const { openRunBuild } = useRunBuild();
  return (
    <Card className="enter min-w-0" style={stagger(4)} data-testid="app-recent-builds">
      <RecentBuildsHeader app={app} project={project} />
      <CardContent className="p-0 sm:p-0">
        <BuildsList
          projectSlug={project.slug}
          builds={rows}
          isLoading={builds.isLoading}
          isError={builds.isError}
          error={builds.error}
          onRetry={() => void builds.refetch()}
          hasMore={false}
          isFetchingMore={false}
          onLoadMore={() => undefined}
          filtered={false}
          onRunBuild={() => openRunBuild({ projectSlug: project.slug, appId: app.id })}
          runBuildGate={gate}
          onReset={() => undefined}
        />
      </CardContent>
    </Card>
  );
}
